import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import ChartContainer from '@aytm/app/stats/page/question-views/components/charts/highcharts/ChartContainer'
import { useChartAxisApi } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useChartAxisApi'

import {
  getChartModeTypes,
  getDefaultChartMode,
  STACKED_VIEW_MODES
} from '@aytm/app/stats/page/question-views/ec/components/chart/lib/chartModes'
import { getAnswer } from '@aytm/app/stats/page/question-views/ec/components/chart/lib/answer'
import { getBatches } from '@aytm/app/stats/page/question-views/ec/components/chart/lib/batches'
import { getFrequencyBatches } from '@aytm/app/stats/page/question-views/ec/components/chart/lib/frequency'
import { getTraitValues } from '@aytm/app/stats/page/question-views/ec/components/chart/lib/traitValues'
import {
  CHART_VIEW_OPTIONS,
  isTraitExcluded
} from '@aytm/app/stats/page/question-views/ec/components/chart/lib/viewOptions'
import { getYAxisTitle, getYAxisLimits } from '@aytm/app/stats/page/question-views/ec/components/chart/lib/yAxis'
import { calcIgnorePercents } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

const EcommerceChart = ({
  questionId,
  questionKey,
  qType,
  viewMode,
  answerVariants,
  answer,
  sortOrder,
  chartMode: chartModeProp,
  questionChartMode,
  globalChartMode,
  onChartModeChange,
  zoomLevel,
  globalZoomLevel,
  onZoomChange,
  isTraitType,
  traitName,
  trait,
  priceTemplate,
  ignorePercents: ignoresPercents = false,
  format,
  colorTheme,
  chartLegendMode,
  confidenceLevel,
  significanceLevel,
  confidenceLevels,
  features,
  onChartReady
}) => {
  const chartRef = useChartAxisApi(onChartReady)

  const answerVariantsCount = answerVariants?.length || 0
  const isTotalPickedUp = viewMode === 'totalPickedUp'
  const isFrequency = viewMode === 'frequency'
  const isStacked = STACKED_VIEW_MODES.includes(viewMode)

  const chartModeTypes = useMemo(
    () => getChartModeTypes({ viewMode, answerVariantsCount }),
    [viewMode, answerVariantsCount]
  )

  const chartMode = useMemo(
    () =>
      getDefaultChartMode({
        chartMode: chartModeProp,
        questionChartMode,
        globalChartMode,
        viewMode,
        answerVariantsCount
      }),
    [chartModeProp, questionChartMode, globalChartMode, viewMode, answerVariantsCount]
  )

  const traitValues = useMemo(() => getTraitValues(trait), [trait])

  const hcAnswers = useMemo(
    () =>
      getAnswer({
        answer,
        answerVariants,
        viewMode,
        priceTemplate,
        trait,
        traitName,
        traitValues
      }),
    [answer, answerVariants, viewMode, priceTemplate, trait, traitName, traitValues]
  )

  const batches = useMemo(() => {
    if (isTotalPickedUp) return getBatches({ answerVariants, trait, traitValues })
    if (isFrequency) return getFrequencyBatches({ answer, answerVariants })
    return null
  }, [isTotalPickedUp, isFrequency, answer, answerVariants, trait, traitValues])

  const yAxisTitle = useMemo(() => getYAxisTitle({ viewMode, chartMode }), [viewMode, chartMode])
  // Split by trait plots one bar per trait value; limits taken from the ungrouped amounts would
  // clip the series actually drawn, so the axis is left to fit itself.
  const isTraitSplit = Boolean(trait) && !isTraitExcluded(viewMode)
  const yAxisLimits = useMemo(
    () => (isTraitSplit ? false : getYAxisLimits({ viewMode, answerVariants, hcAnswers })),
    [isTraitSplit, viewMode, answerVariants, hcAnswers]
  )

  const chartType = isStacked ? `stacked_${chartMode}` : chartMode

  // the shares the chart leaves out: the question's own, and every view but Amount, which has none
  const ignorePercents = calcIgnorePercents(format, ignoresPercents) || viewMode !== 'amount'

  const viewOptionsDisabled = features?.view_options?.disabled

  return (
    <ChartContainer
      chartRef={chartRef}
      questionId={questionId}
      questionKey={questionKey}
      qType={qType}
      chartMode={chartType}
      selectorChartModeValue={chartMode}
      chartModeOverride={chartModeProp}
      globalChartMode={globalChartMode}
      chartModeTypes={chartModeTypes}
      viewOptionsDisabled={viewOptionsDisabled}
      onChartModeChange={onChartModeChange}
      answerVariants={isStacked ? null : answerVariants}
      batches={batches}
      answer={hcAnswers}
      sortOrder={sortOrder}
      yAxisTitle={yAxisTitle}
      yAxisLimits={yAxisLimits}
      zoomLevel={zoomLevel}
      globalZoomLevel={globalZoomLevel}
      zoomType={!isStacked}
      onZoomChange={onZoomChange}
      traitName={traitName}
      trait={trait}
      traitValues={traitValues}
      ignoreTraits={format?.ignoreTraits === true || !isTraitType}
      // names only: a family's codes are not the flat position, and the coded, tree-ordered surface is the table
      ignoreRefs
      ignorePercents={ignorePercents}
      viewMode={viewMode}
      colorTheme={colorTheme}
      chartLegendMode={chartLegendMode}
      format={format}
      confidenceLevel={confidenceLevel}
      significanceLevel={significanceLevel}
      confidenceLevels={confidenceLevels}
      features={features}
    />
  )
}

EcommerceChart.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  qType: PropTypes.string.isRequired,
  viewMode: PropTypes.oneOf(CHART_VIEW_OPTIONS.map(o => o.value)).isRequired,
  answerVariants: PropTypes.array,
  answer: answerPropType,
  sortOrder: PropTypes.string,
  chartMode: PropTypes.string,
  questionChartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  onChartModeChange: PropTypes.func.isRequired,
  zoomLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  globalZoomLevel: PropTypes.string,
  onZoomChange: PropTypes.func,
  isTraitType: PropTypes.bool,
  traitName: PropTypes.string,
  trait: PropTypes.object,
  priceTemplate: PropTypes.string,
  ignorePercents: PropTypes.bool,
  format: PropTypes.object,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  confidenceLevel: PropTypes.number,
  significanceLevel: PropTypes.number,
  confidenceLevels: PropTypes.array,
  features: PropTypes.object,
  onChartReady: PropTypes.func
}

EcommerceChart.displayName = 'EcommerceChart'

export default EcommerceChart
